import { useState, useCallback, useMemo } from "react";
import {
  Modal,
  Stack,
  Text,
  Group,
  Button,
  Badge,
  Alert,
  Checkbox,
  Divider,
  Menu,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconMessageCircle,
  IconExternalLink,
  IconCheck,
  IconX,
  IconRefresh,
  IconChevronDown,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  DiscussionCategory,
  discussionCategoryLabels,
  discussionCategoryColors,
  discussionCategoryAbbreviations,
} from "../../services/internalDiscussions.service";
import type { RequiredDiscussionTrigger } from "../../services/signOff.service";
import { useCreateDiscussion } from "../../hooks/useInternalDiscussions";

type CreationStatus = "idle" | "loading" | "success" | "error";

interface DiscussionRequiredModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onGoToDiscussion?: () => void;
  onSignOutAfterCreating?: () => void;
  onSignOutIgnoring?: () => void;
  caseId: string;
  missingDiscussions: RequiredDiscussionTrigger[];
  existingDiscussions?: Array<{
    discussionId: string;
    category: string;
    status: string;
    isPending: boolean;
  }>;
}

/**
 * Modal shown when sign-out is blocked due to required discussion triggers.
 * Per C-813 Section 7.2: Discussion Required modal.
 *
 * Self-contained: imports useCreateDiscussion and creates discussions
 * for all selected categories in parallel via Promise.allSettled.
 *
 * Handles two independent blocking conditions:
 * - Missing discussions: categories that need to be created (with checkboxes)
 * - Pending discussions: existing discussions that need resolution
 * Both can appear simultaneously (different categories).
 */
export function DiscussionRequiredModal({
  opened,
  onClose,
  onSuccess,
  onGoToDiscussion,
  onSignOutAfterCreating,
  onSignOutIgnoring,
  caseId,
  missingDiscussions,
  existingDiscussions = [],
}: DiscussionRequiredModalProps) {
  const createDiscussion = useCreateDiscussion();

  const pendingDiscussions = existingDiscussions.filter((d) => d.isPending);
  const hasPendingDiscussion = pendingDiscussions.length > 0;
  const hasMissingDiscussions = missingDiscussions.length > 0;

  const pendingCategories = useMemo(
    () => new Set(pendingDiscussions.map((d) => d.category)),
    [pendingDiscussions]
  );

  // Categories that need creation (not already pending)
  const creatableCategories = useMemo(
    () => missingDiscussions.filter((t) => !pendingCategories.has(t.category)),
    [missingDiscussions, pendingCategories]
  );

  // Local state for checkbox selection and creation status
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(
    () => new Set(creatableCategories.map((t) => t.category))
  );
  const [creationStatus, setCreationStatus] = useState<
    Map<string, CreationStatus>
  >(new Map());
  const [isCreating, setIsCreating] = useState(false);

  // Reset state when modal opens with new data
  const resetState = useCallback(() => {
    const newSelected = new Set(creatableCategories.map((t) => t.category));
    setSelectedCategories(newSelected);
    setCreationStatus(new Map());
    setIsCreating(false);
  }, [creatableCategories]);

  // Reset when opened changes
  useState(() => {
    if (opened) {
      resetState();
    }
  });

  const toggleCategory = (category: string) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const hasFailedCategories = useMemo(
    () =>
      Array.from(creationStatus.values()).some((status) => status === "error"),
    [creationStatus]
  );

  const selectedCount = selectedCategories.size;

  const handleCreateDiscussions = async (onAllSucceeded?: () => void) => {
    // Validate: at least one category must be selected (CONV-001: validate on click, not disable)
    if (selectedCount === 0) {
      notifications.show({
        title: "No Categories Selected",
        message: "Please select at least one discussion category to create.",
        color: "orange",
      });
      return;
    }

    const categoriesToCreate = creatableCategories.filter(
      (t) =>
        selectedCategories.has(t.category) &&
        creationStatus.get(t.category) !== "success"
    );

    if (categoriesToCreate.length === 0) return;

    setIsCreating(true);

    // Set all selected to loading
    const newStatus = new Map(creationStatus);
    categoriesToCreate.forEach((t) => newStatus.set(t.category, "loading"));
    setCreationStatus(newStatus);

    const results = await Promise.allSettled(
      categoriesToCreate.map((trigger) =>
        createDiscussion.mutateAsync({
          caseId,
          category: trigger.category as DiscussionCategory,
          initialComment: trigger.reason,
        })
      )
    );

    // Update status per result
    const finalStatus = new Map(creationStatus);
    categoriesToCreate.forEach((trigger, index) => {
      finalStatus.set(
        trigger.category,
        results[index].status === "fulfilled" ? "success" : "error"
      );
    });
    setCreationStatus(finalStatus);
    setIsCreating(false);

    const allSucceeded = results.every((r) => r.status === "fulfilled");
    const someSucceeded = results.some((r) => r.status === "fulfilled");
    const successCallback = onAllSucceeded ?? onSuccess;

    if (allSucceeded) {
      notifications.show({
        title: "Discussions Created",
        message: `${results.length} discussion${
          results.length > 1 ? "s" : ""
        } created successfully.`,
        color: "green",
      });
      successCallback();
    } else if (someSucceeded) {
      const failedCount = results.filter((r) => r.status === "rejected").length;
      notifications.show({
        title: "Partial Success",
        message: `${failedCount} discussion${
          failedCount > 1 ? "s" : ""
        } failed to create. You can retry the failed ones.`,
        color: "orange",
      });
    } else {
      notifications.show({
        title: "Creation Failed",
        message: "All discussions failed to create. Please try again.",
        color: "red",
      });
    }
  };

  const handleRetryFailed = async () => {
    const failedCategories = creatableCategories.filter(
      (t) => creationStatus.get(t.category) === "error"
    );

    if (failedCategories.length === 0) return;

    setIsCreating(true);

    const newStatus = new Map(creationStatus);
    failedCategories.forEach((t) => newStatus.set(t.category, "loading"));
    setCreationStatus(newStatus);

    const results = await Promise.allSettled(
      failedCategories.map((trigger) =>
        createDiscussion.mutateAsync({
          caseId,
          category: trigger.category as DiscussionCategory,
          initialComment: trigger.reason,
        })
      )
    );

    const finalStatus = new Map(creationStatus);
    failedCategories.forEach((trigger, index) => {
      finalStatus.set(
        trigger.category,
        results[index].status === "fulfilled" ? "success" : "error"
      );
    });
    setCreationStatus(finalStatus);
    setIsCreating(false);

    const allSucceeded = results.every((r) => r.status === "fulfilled");

    if (allSucceeded) {
      // Check if ALL categories are now successful
      const allDone = creatableCategories.every(
        (t) =>
          finalStatus.get(t.category) === "success" ||
          !selectedCategories.has(t.category)
      );
      if (allDone) {
        notifications.show({
          title: "Discussions Created",
          message: "All discussions created successfully.",
          color: "green",
        });
        onSuccess();
      } else {
        notifications.show({
          title: "Retry Successful",
          message: "Previously failed discussions have been created.",
          color: "green",
        });
      }
    } else {
      notifications.show({
        title: "Retry Failed",
        message: "Some discussions still failed. Please try again.",
        color: "red",
      });
    }
  };

  const getStatusBadge = (category: string) => {
    const status = creationStatus.get(category);
    switch (status) {
      case "loading":
        return (
          <Badge color="blue" variant="outline" size="xs">
            Creating...
          </Badge>
        );
      case "success":
        return (
          <Badge
            color="green"
            variant="light"
            size="xs"
            leftSection={<IconCheck size={10} />}
          >
            Created
          </Badge>
        );
      case "error":
        return (
          <Badge
            color="red"
            variant="light"
            size="xs"
            leftSection={<IconX size={10} />}
          >
            Failed
          </Badge>
        );
      default:
        return null;
    }
  };

  const hasCreatable = creatableCategories.length > 0;
  const allUnselected = hasCreatable && selectedCount === 0;

  // Primary button — always Sign Out; label changes when allUnselected
  const primaryLabel = allUnselected
    ? "Sign Out"
    : "Sign Out and Ignore Warning";
  const handlePrimary = () => onSignOutIgnoring?.();

  // Dropdown items — secondary options shown under the chevron
  const showCreateAndSignOut =
    hasCreatable &&
    !hasFailedCategories &&
    !allUnselected &&
    !!onSignOutAfterCreating;
  const showCreateDiscussions =
    hasCreatable && !allUnselected && !hasFailedCategories;
  const showGoToQueue = hasPendingDiscussion && !!onGoToDiscussion;

  const hasDropdownItems =
    showCreateAndSignOut ||
    showCreateDiscussions ||
    showGoToQueue ||
    hasFailedCategories;

  return (
    <Modal.Root
      opened={opened}
      onClose={onClose}
      size="xl"
      centered
      closeOnClickOutside={!isCreating}
      closeOnEscape={!isCreating}
      styles={{
        content: {
          display: "flex",
          flexDirection: "column",
          maxHeight: "85vh",
        },
      }}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>
            <Group gap="xs">
              <IconAlertTriangle
                size={20}
                color="var(--mantine-color-orange-6)"
              />
              <span>Discussion Required</span>
            </Group>
          </Modal.Title>
          <Modal.CloseButton disabled={isCreating} />
        </Modal.Header>

        <Modal.Body style={{ flex: 1, overflow: "auto" }}>
          <Stack gap="md">
            <Alert
              color="orange"
              icon={<IconAlertTriangle size={20} />}
              title="This case requires internal discussion before sign-out"
            >
              <Text size="sm">
                {hasMissingDiscussions && hasPendingDiscussion
                  ? "Some discussion categories still need to be created, and existing discussions need resolution."
                  : hasPendingDiscussion
                  ? "There are pending discussions that must be resolved before the case can be signed out."
                  : "Based on the diagnosis criteria, this case requires peer review before it can be signed out. Select the categories below and create discussions."}
              </Text>
            </Alert>

            {/* Missing discussions with checkboxes */}
            {creatableCategories.length > 0 && (
              <Stack gap="xs">
                <Text fw={500} size="sm">
                  Required Discussion Categories:
                </Text>
                <Stack gap="sm">
                  {creatableCategories.map((trigger) => {
                    const category = trigger.category as DiscussionCategory;
                    const label =
                      discussionCategoryLabels[category] || category;
                    const color = discussionCategoryColors[category] || "gray";
                    const abbrev =
                      discussionCategoryAbbreviations[category] || category;
                    const isSelected = selectedCategories.has(trigger.category);
                    const status = creationStatus.get(trigger.category);
                    const isCompleted = status === "success";

                    return (
                      <Group key={trigger.category} gap="xs" wrap="nowrap">
                        <Checkbox
                          checked={isSelected}
                          onChange={() => toggleCategory(trigger.category)}
                          disabled={isCreating || isCompleted}
                        />
                        <Badge color={color} variant="light" size="sm">
                          {abbrev}
                        </Badge>
                        <Text size="sm" style={{ flex: 1 }}>
                          {label} — {trigger.reason}
                        </Text>
                        {getStatusBadge(trigger.category)}
                      </Group>
                    );
                  })}
                </Stack>
              </Stack>
            )}

            {/* Already-pending categories from missingDiscussions (already flagged but not resolved) */}
            {missingDiscussions.some((t) =>
              pendingCategories.has(t.category)
            ) && (
              <Stack gap="xs">
                <Text fw={500} size="sm">
                  Already Flagged (Pending Resolution):
                </Text>
                <Stack gap="sm">
                  {missingDiscussions
                    .filter((t) => pendingCategories.has(t.category))
                    .map((trigger) => {
                      const category = trigger.category as DiscussionCategory;
                      const label =
                        discussionCategoryLabels[category] || category;
                      const color =
                        discussionCategoryColors[category] || "gray";
                      const abbrev =
                        discussionCategoryAbbreviations[category] || category;

                      return (
                        <Group key={trigger.category} gap="xs" wrap="nowrap">
                          <Badge color={color} variant="light" size="sm">
                            {abbrev}
                          </Badge>
                          <Text size="sm" style={{ flex: 1 }}>
                            {label}
                          </Text>
                          <Badge color="yellow" variant="outline" size="xs">
                            Pending
                          </Badge>
                        </Group>
                      );
                    })}
                </Stack>
              </Stack>
            )}

            {/* Existing pending discussions not in missingDiscussions */}
            {pendingDiscussions.some(
              (d) => !missingDiscussions.some((t) => t.category === d.category)
            ) && (
              <Stack gap="xs">
                <Text fw={500} size="sm">
                  Pending Discussions:
                </Text>
                <Stack gap="sm">
                  {pendingDiscussions
                    .filter(
                      (d) =>
                        !missingDiscussions.some(
                          (t) => t.category === d.category
                        )
                    )
                    .map((discussion) => {
                      const category =
                        discussion.category as DiscussionCategory;
                      const label =
                        discussionCategoryLabels[category] || category;
                      const color =
                        discussionCategoryColors[category] || "gray";
                      const abbrev =
                        discussionCategoryAbbreviations[category] || category;

                      return (
                        <Group
                          key={discussion.discussionId}
                          gap="xs"
                          wrap="nowrap"
                        >
                          <Badge color={color} variant="light" size="sm">
                            {abbrev}
                          </Badge>
                          <Text size="sm" style={{ flex: 1 }}>
                            {label}
                          </Text>
                          <Badge color="yellow" variant="outline" size="xs">
                            Pending
                          </Badge>
                        </Group>
                      );
                    })}
                </Stack>
              </Stack>
            )}

            <Text size="sm" c="dimmed">
              {hasMissingDiscussions && !hasPendingDiscussion
                ? "The case cannot be signed out until the required discussions are completed."
                : hasPendingDiscussion && creatableCategories.length === 0
                ? "Please resolve the pending discussion(s) before attempting to sign out."
                : "Please create the missing discussions and resolve all pending discussions before sign-out."}
            </Text>
          </Stack>
        </Modal.Body>

        {/* Footer */}
        <Divider />
        <Group justify="space-between" p="md">
          {/* Left: Close */}
          <Button
            size="sm"
            variant="subtle"
            onClick={onClose}
            disabled={isCreating}
          >
            Close
          </Button>

          {/* Right: split button — primary action + dropdown for secondary options */}
          <Button.Group>
            <Button
              size="sm"
              color="red"
              onClick={handlePrimary}
              disabled={isCreating || !onSignOutIgnoring}
            >
              {primaryLabel}
            </Button>

            {hasDropdownItems && (
              <Menu withinPortal position="bottom-end">
                <Menu.Target>
                  <Button size="sm" color="red" px="xs" disabled={isCreating}>
                    <IconChevronDown size={14} />
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  {showCreateAndSignOut && (
                    <Menu.Item
                      leftSection={<IconMessageCircle size={14} />}
                      onClick={() =>
                        handleCreateDiscussions(onSignOutAfterCreating)
                      }
                    >
                      Create Discussions and Sign Out
                    </Menu.Item>
                  )}
                  {showCreateDiscussions && (
                    <Menu.Item
                      leftSection={<IconMessageCircle size={14} />}
                      onClick={() => handleCreateDiscussions()}
                    >
                      {selectedCount > 0
                        ? `Create ${selectedCount} Discussion${
                            selectedCount > 1 ? "s" : ""
                          }`
                        : "Create Discussions"}
                    </Menu.Item>
                  )}
                  {showGoToQueue && (
                    <Menu.Item
                      leftSection={<IconExternalLink size={14} />}
                      onClick={onGoToDiscussion}
                    >
                      Go to Discussion Queue
                    </Menu.Item>
                  )}
                  {hasFailedCategories && (
                    <Menu.Item
                      color="orange"
                      leftSection={<IconRefresh size={14} />}
                      onClick={handleRetryFailed}
                    >
                      Retry Failed
                    </Menu.Item>
                  )}
                </Menu.Dropdown>
              </Menu>
            )}
          </Button.Group>
        </Group>
      </Modal.Content>
    </Modal.Root>
  );
}
